import { z } from 'zod';
import { IsoDateTime, Uuid } from '../primitives.js';

/**
 * WS-13 Feature 3b — RECURRING MEMBERSHIPS (LRU-14, the other half of packages). A `£X/month`
 * subscription on the SALON's CONNECTED Stripe account, sold against a `membership_recurring` package
 * (entities/packages.ts), built behind an INERT gateway so it ships gate-green WITHOUT Stripe keys
 * (same posture as WS-8 deposits / WS-10 SaaS billing).
 *
 * THE CONNECT-PLANE SEPARATION: the membership subscription acts on the salon's CONNECTED account — the
 * gateway always sets the Stripe-Account header — using the PLATFORM Stripe secret (the SAME key the
 * payments Connect gateway uses). It is STRUCTURALLY SEPARATE from the WS-10 platform billing plane
 * (entities/billing.ts), which never touches a connected account.
 *
 * The member benefit is the package's item discount_bps frozen at sale onto benefitDiscountBps; the till
 * applies it as a standard `discount` line with reasonCode 'membership' (entities/discounts.ts). Money is
 * integer pence; discounts are basis points.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/**
 * The membership subscription status, mirroring Stripe's subscription lifecycle (a reconciled cache of
 * the connected-account subscription). 'incomplete' = created but the first charge has not confirmed (the
 * default a synthetic/fake subscription may land in until a webhook advances it); 'trialing'/'active' =
 * live (benefit applies); 'past_due' = a charge failed; 'canceled' = terminal (re-subscribable).
 */
export const MembershipStatus = z.enum([
  'trialing',
  'active',
  'past_due',
  'canceled',
  'incomplete',
]);
export type MembershipStatus = z.infer<typeof MembershipStatus>;

// ── Entity (staff/till view) ───────────────────────────────────────────────────

/**
 * ONE recurring membership a client holds — the connected-account subscription state. The stripe ids are
 * NULL under the fake gateway (synthetic/inert on staging — no charge). benefitDiscountBps is FROZEN from
 * the package at sale (the member benefit applied at the till while the membership is active).
 */
export const ClientMembership = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable().describe('the book that owns it; null for the floor'),
    clientId: Uuid,
    packageId: Uuid.describe('the membership_recurring package SKU this subscription is for'),
    status: MembershipStatus,
    /** The mirrored connected-account subscription id (synthetic under the fake gateway; null when none). */
    stripeSubscriptionId: z.string().nullable(),
    stripeCustomerId: z.string().nullable(),
    currentPeriodEnd: IsoDateTime.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    /** The member benefit frozen at sale: a discount in basis points applied at the till while active. */
    benefitDiscountBps: z.int().min(0).max(10000).nullable(),
    startedAt: IsoDateTime,
    canceledAt: IsoDateTime.nullable(),
    createdByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'ClientMembership' });
export type ClientMembership = z.infer<typeof ClientMembership>;

// ── Requests ─────────────────────────────────────────────────────────────────

/**
 * SELL a recurring membership to a client — create the subscription on the salon's CONNECTED account via
 * the gateway (FAKE → synthetic, inert on staging: NO charge), freeze benefitDiscountBps from the package,
 * insert client_memberships. Idempotent on `idempotencyKey`. `paymentMethodId` is the client's saved card
 * on the connected account (optional under the fake gateway). The package must be `membership_recurring`.
 */
export const SellMembership = z
  .object({
    clientId: Uuid,
    packageId: Uuid.describe('a membership_recurring catalogue package'),
    /** A card/payment-method id on the salon's connected account (optional under the fake gateway). */
    paymentMethodId: z.string().min(1).max(200).optional(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'SellMembership' });
export type SellMembership = z.infer<typeof SellMembership>;

// ── Query schemas (NO .meta — entity-only carry .meta({ id })) ─────────────────

export const ListClientMembershipsQuery = z.object({
  clientId: Uuid.optional(),
  status: MembershipStatus.optional(),
});
export type ListClientMembershipsQuery = z.infer<typeof ListClientMembershipsQuery>;

// ── Client portal DTO (NO internal/stripe ids — customer-facing only) ──────────

/**
 * The client-portal "Membership" view for ONE recurring membership. Customer-facing ONLY: NO clientId /
 * packageId / renterId / stripe ids — the salonName + the membership name + status + renewal date + the
 * member benefit (bps). Read-only.
 */
export const ClientMembershipView = z
  .object({
    salonName: z.string(),
    name: z.string().describe('the membership package name'),
    status: MembershipStatus,
    /** The next renewal date (current period end); null until the first cycle confirms. */
    currentPeriodEnd: IsoDateTime.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    /** The member benefit (a discount in basis points) the salon applies at the till while active. */
    benefitDiscountBps: z.int().min(0).max(10000).nullable(),
    startedAt: IsoDateTime,
  })
  .meta({ id: 'ClientMembershipView' });
export type ClientMembershipView = z.infer<typeof ClientMembershipView>;
